import type { TripMode, TripState, RouteStop } from '@/types';
import { STATION_INDEX } from '@/data/graph';
import { LS_KEY, DEFAULT_STATE } from './constants';

/**
 * Persistence : localStorage + URL params.
 *
 * Format URL d'une étape : `id[-nights][-tHHMM][-mMODE]`
 *   - nights : nombre de nuits sur place (omis si 0)
 *   - tHHMM  : override d'heure de départ depuis cette étape (omis si absent)
 *   - mMODE  : override de mode pour le segment partant (omis si absent)
 *              MODE ∈ {train, bus, best, night}
 *
 * Exemples :
 *   - `lyon`                 → simple
 *   - `lyon-2`               → 2 nuits
 *   - `lyon-2-t1030`         → 2 nuits, départ à 10h30
 *   - `lyon-2-t1030-mbus`    → idem + override bus
 *   - `lyon-0-mbus`          → 0 nuit, override bus seul
 *   - `lyon--mbus`           → 0 nuit (omis), override bus seul (forme compacte alternative)
 *
 * Toutes les fonctions sont DÉFENSIVES : si une station n'existe plus
 * (données mises à jour après une sauvegarde), elle est silencieusement écartée.
 * Les anciens liens (`lyon-2`) restent parfaitement compatibles.
 */

const VALID_MODES: TripMode[] = ['best', 'train', 'bus', 'night'];

function isValidMode(m: string): m is TripMode {
  return (VALID_MODES as string[]).includes(m);
}

/**
 * Encode une RouteStop en chaîne compacte URL-safe.
 * Le séparateur `-` est sûr : ne peut pas apparaître dans un id (kebab-lowercase),
 * et les sous-tokens `t` / `m` lèvent toute ambiguïté.
 */
function encodeStop(s: RouteStop): string {
  const parts: string[] = [s.id];
  const hasNights = s.nights && s.nights > 0;
  const hasTime = !!s.startTime;
  const hasMode = !!s.modeOverride;

  if (hasNights || hasTime || hasMode) {
    parts.push(hasNights ? String(s.nights) : '0');
  }
  if (hasTime) {
    // "10:30" → "t1030"
    parts.push('t' + s.startTime!.replace(':', ''));
  }
  if (hasMode) {
    parts.push('m' + s.modeOverride);
  }
  return parts.join('-');
}

/**
 * Décode une chaîne `id-nights-tHHMM-mMODE` en RouteStop.
 * Renvoie `null` si l'id est inconnu.
 * Tokens inconnus / mal formés sont silencieusement ignorés.
 */
function decodeStop(part: string): RouteStop | null {
  const tokens = part.split('-');
  const id = tokens[0];
  if (!id || STATION_INDEX[id] === undefined) return null;

  const stop: RouteStop = { id, nights: 0 };

  for (let i = 1; i < tokens.length; i++) {
    const tok = tokens[i];
    if (tok === '') continue;
    if (tok.startsWith('t')) {
      const raw = tok.slice(1);
      // attend 4 chiffres "HHMM"
      if (/^\d{4}$/.test(raw)) {
        const hh = raw.slice(0, 2);
        const mm = raw.slice(2, 4);
        const h = parseInt(hh, 10);
        const m = parseInt(mm, 10);
        if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
          stop.startTime = `${hh}:${mm}`;
        }
      }
    } else if (tok.startsWith('m')) {
      const mode = tok.slice(1);
      if (isValidMode(mode)) stop.modeOverride = mode;
    } else if (/^\d+$/.test(tok)) {
      // Premier token numérique pur = nuits
      const n = parseInt(tok, 10);
      if (!isNaN(n)) stop.nights = Math.max(0, n);
    }
    // Sinon : token inconnu, ignoré (forward-compat)
  }

  return stop;
}

/** Encode l'état utilisateur en URLSearchParams compacts */
export function encodeRouteToParams(state: TripState): URLSearchParams {
  const parts = state.routeStops.map(encodeStop);
  const params = new URLSearchParams();
  if (parts.length) params.set('route', parts.join(','));
  if (state.routeMode !== 'best') params.set('mode', state.routeMode);
  if (state.tripStartDate) params.set('date', state.tripStartDate);
  if (state.tripStartTime && state.tripStartTime !== DEFAULT_STATE.tripStartTime) {
    params.set('time', state.tripStartTime);
  }
  // Filtres carte : on n'écrit dans l'URL que si différent de la valeur par défaut
  // pour garder les liens compacts. La valeur par défaut sera restaurée à la lecture.
  if (state.maxHours !== DEFAULT_STATE.maxHours) {
    params.set('max', String(state.maxHours));
  }
  if (state.routeDirectOnly !== DEFAULT_STATE.routeDirectOnly) {
    params.set('direct', '1');
  }
  return params;
}

/** Construit une URL partageable absolue (basée sur window.location) */
export function buildShareURL(state: TripState): string {
  const params = encodeRouteToParams(state);
  const base = window.location.origin + window.location.pathname;
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * Charge un état partiel depuis une querystring.
 * Mute `state` en place uniquement pour les champs PRÉSENTS et VALIDES.
 * Renvoie `true` si au moins un champ a été chargé.
 */
export function loadFromParams(state: TripState, searchString: string): boolean {
  const params = new URLSearchParams(searchString);
  let loaded = false;

  const routeStr = params.get('route');
  if (routeStr) {
    const stops: RouteStop[] = [];
    routeStr.split(',').forEach((part) => {
      const decoded = decodeStop(part);
      if (decoded) {
        // Déduplication des doublons consécutifs : un vieux lien partagé
        // peut contenir `paris,paris,rome` à cause d'un bug antérieur où
        // `addStop` acceptait les doublons. On nettoie silencieusement.
        const last = stops[stops.length - 1];
        if (last && last.id === decoded.id) return;
        stops.push(decoded);
      }
    });
    if (stops.length) {
      state.routeStops = stops;
      loaded = true;
    }
  }

  const mode = params.get('mode');
  if (mode && isValidMode(mode)) {
    state.routeMode = mode;
    loaded = true;
  }

  const date = params.get('date');
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    state.tripStartDate = date;
    loaded = true;
  }

  const time = params.get('time');
  if (time && /^\d{2}:\d{2}$/.test(time)) {
    state.tripStartTime = time;
    loaded = true;
  }

  const max = params.get('max');
  if (max && /^\d+$/.test(max)) {
    const n = parseInt(max, 10);
    if (n >= 1 && n <= 24) {
      state.maxHours = n;
      loaded = true;
    }
  }

  const direct = params.get('direct');
  if (direct === '1') {
    state.routeDirectOnly = true;
    loaded = true;
  }

  return loaded;
}

/** Sauvegarde l'état dans localStorage (silencieux si indisponible) */
export function saveToLocalStorage(state: TripState): void {
  try {
    const data = {
      routeStops: state.routeStops,
      routeMode: state.routeMode,
      routeDirectOnly: state.routeDirectOnly,
      maxHours: state.maxHours,
      origin: state.origin,
      tripStartDate: state.tripStartDate,
      tripStartTime: state.tripStartTime,
    };
    localStorage.setItem(LS_KEY, JSON.stringify(data));
  } catch {
    // localStorage indisponible (mode privé, quota dépassé) : on continue silencieusement
  }
}

/**
 * Restaure l'état depuis localStorage.
 * Renvoie `true` si quelque chose a été chargé.
 */
export function loadFromLocalStorage(state: TripState): boolean {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);

    if (Array.isArray(data.routeStops)) {
      state.routeStops = data.routeStops
        .filter((s: unknown): s is { id: string; nights?: unknown; startTime?: unknown; modeOverride?: unknown } => {
          return !!s && typeof s === 'object' && 'id' in s && STATION_INDEX[(s as { id: string }).id] !== undefined;
        })
        .map((s: { id: string; nights?: unknown; startTime?: unknown; modeOverride?: unknown }) => {
          const out: RouteStop = {
            id: s.id,
            nights: typeof s.nights === 'number' ? Math.max(0, s.nights) : 0,
          };
          if (typeof s.startTime === 'string' && /^\d{2}:\d{2}$/.test(s.startTime)) {
            out.startTime = s.startTime;
          }
          if (typeof s.modeOverride === 'string' && isValidMode(s.modeOverride)) {
            out.modeOverride = s.modeOverride;
          }
          return out;
        });
    }
    if (typeof data.routeMode === 'string' && isValidMode(data.routeMode)) {
      state.routeMode = data.routeMode;
    }
    if (typeof data.routeDirectOnly === 'boolean') {
      state.routeDirectOnly = data.routeDirectOnly;
    }
    if (typeof data.maxHours === 'number' && data.maxHours >= 1 && data.maxHours <= 24) {
      state.maxHours = data.maxHours;
    }
    if (typeof data.origin === 'string' && STATION_INDEX[data.origin] !== undefined) {
      state.origin = data.origin;
    }
    if (typeof data.tripStartDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(data.tripStartDate)) {
      state.tripStartDate = data.tripStartDate;
    }
    if (typeof data.tripStartTime === 'string' && /^\d{2}:\d{2}$/.test(data.tripStartTime)) {
      state.tripStartTime = data.tripStartTime;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Met à jour l'URL sans recharger la page (history.replaceState).
 * À appeler après chaque mutation d'état. Debouncé via le composable.
 */
export function updateURL(state: TripState): void {
  const params = encodeRouteToParams(state);
  const qs = params.toString();
  const newUrl = qs ? `?${qs}` : window.location.pathname;
  history.replaceState(null, '', newUrl);
}
